import { useTranslation } from 'react-i18next';
import { Briefcase } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContextualHint } from '../components';

// Common service durations in minutes
const DURATION_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1h 30min' },
  { value: '120', label: '2 hours' },
];

interface CreateServiceStepProps {
  serviceName: string;
  onServiceNameChange: (name: string) => void;
  duration: number;
  onDurationChange: (duration: number) => void;
  price: string;
  onPriceChange: (price: string) => void;
  currency?: string;
}

export function CreateServiceStep({
  serviceName,
  onServiceNameChange,
  duration,
  onDurationChange,
  price,
  onPriceChange,
  currency = 'EUR',
}: CreateServiceStepProps) {
  const { t } = useTranslation('staff');

  const currencySymbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
          <Briefcase className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('availability.wizard.createService.title', 'Create a Service')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('availability.wizard.createService.subtitle', 'What do you offer?')}
          </p>
        </div>
      </div>

      {/* Hint */}
      <ContextualHint variant="explanation">
        {t(
          'availability.wizard.hints.service',
          'A service is what customers book - like a haircut, consultation, or massage. You set the name, how long it takes, and the price.'
        )}
      </ContextualHint>

      {/* Service name */}
      <div className="space-y-2">
        <Label htmlFor="service-name" className="text-sm font-medium">
          {t('availability.wizard.createService.name', 'Service name')}
        </Label>
        <Input
          id="service-name"
          placeholder={t(
            'availability.wizard.createService.namePlaceholder',
            'e.g., Haircut, Consultation, Massage'
          )}
          value={serviceName}
          onChange={(e) => onServiceNameChange(e.target.value)}
          className="min-h-[48px] text-base"
        />
      </div>

      {/* Duration */}
      <div className="space-y-2">
        <Label htmlFor="service-duration" className="text-sm font-medium">
          {t('availability.wizard.createService.duration', 'How long does it take?')}
        </Label>
        <Select
          value={String(duration)}
          onValueChange={(val) => onDurationChange(Number(val))}
        >
          <SelectTrigger id="service-duration" className="min-h-[48px] text-base">
            <SelectValue placeholder={t('availability.wizard.createService.selectDuration', 'Select duration')} />
          </SelectTrigger>
          <SelectContent>
            {DURATION_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="py-3">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price */}
      <div className="space-y-2">
        <Label htmlFor="service-price" className="text-sm font-medium">
          {t('availability.wizard.createService.price', 'Price')}
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">
            {currencySymbol}
          </span>
          <Input
            id="service-price"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
            className="min-h-[48px] text-base pl-8"
          />
        </div>
        <p className="text-xs text-gray-500">
          {t(
            'availability.wizard.createService.priceHint',
            'Leave empty or 0 for free services'
          )}
        </p>
      </div>
    </div>
  );
}
